/**
 * A typed conversation. Story VC-016, descended from VC-012.
 *
 * `agent_runtime_lld.md` §24.4's endpoints and nothing else: no WebSocket to
 * `/v1/voice`, no speech provider, and no microphone. A deployment with no
 * provider credentials can still hold this conversation.
 *
 * There is no turn state here. The runtime's events *are* the turn state
 * (`useConversation`), so this file reads state, renders it, and posts what
 * is typed.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { closeSession } from "../api/runtime.js";
import { Icon } from "../components/Icon.jsx";
import { Thread } from "../components/Thread.jsx";
import { useConversation } from "../session/useConversation.js";
import { rememberSession } from "../session/store.js";

/**
 * @param {{ config: any, sessionId: string, makeDecoder: any,
 *           initialTurns: any[], onHome: () => void,
 *           onInspect: () => void }} props
 */
export function Chat({
  config,
  sessionId,
  makeDecoder,
  initialTurns,
  onHome,
  onInspect,
}) {
  const [draft, setDraft] = useState("");
  const [ending, setEnding] = useState(false);
  const scroll = useRef(null);
  const conversation = useConversation({
    config,
    sessionId,
    makeDecoder,
    initialTurns,
  });
  const { turns, busy, closed, connection, error, send } = conversation;

  // Follow the conversation, but only when the reader is already at the
  // bottom. Scrolling someone back down while they are reading an earlier
  // answer is the thing every chat client gets wrong once.
  useEffect(() => {
    const element = scroll.current;
    if (!element) {
      return;
    }
    const atBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 120;
    if (atBottom) {
      element.scrollTop = element.scrollHeight;
    }
  }, [turns]);

  const submit = useCallback(
    async (text) => {
      setDraft("");
      try {
        await send(text);
      } catch {
        // `send` put the message back and said why. Restoring the box is
        // what makes that promise complete.
        setDraft(text);
      }
    },
    [send],
  );

  const end = useCallback(async () => {
    setEnding(true);
    try {
      await closeSession(config, sessionId);
      rememberSession(sessionId, { ended: true });
    } catch {
      // The session may already be gone, or the server unreachable. Going
      // home either way: a user who pressed "end" has ended it as far as
      // they are concerned, and §7.2's sweeper closes what this could not.
    }
    onHome();
  }, [config, onHome, sessionId]);

  return (
    <div className="convo">
      <header className="appbar">
        <button
          type="button"
          className="icon-btn"
          onClick={onHome}
          aria-label="Back"
        >
          <Icon name="back" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="appbar__title">Chat with Agent</h1>
          <div className="appbar__subtitle">
            <span
              className={`dot ${
                connection === "error"
                  ? "dot--error"
                  : busy
                    ? "dot--working"
                    : connection === "live"
                      ? "dot--live"
                      : ""
              }`}
              style={{ display: "inline-block", marginRight: "0.375rem" }}
            />
            {closed ? "ended" : busy ? "thinking" : connection}
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={onInspect}
          aria-label="What happened inside this session"
          title="What happened inside this session"
        >
          <Icon name="pulse" size={20} />
        </button>
        <button
          type="button"
          className="btn btn--danger btn--text"
          onClick={end}
          disabled={ending || closed}
        >
          End
        </button>
      </header>

      <div className="convo__scroll" ref={scroll}>
        <Thread
          turns={turns}
          onChoose={submit}
          busy={busy}
          emptyMessage="Ask about an order, an appointment, or anything else this desk handles."
        />
        {error ? (
          <p className="status status--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          if (draft.trim()) {
            submit(draft);
          }
        }}
      >
        <div className="composer__field">
          <textarea
            className="composer__input"
            rows={1}
            value={draft}
            placeholder={closed ? "This conversation has ended" : "Type a message"}
            disabled={closed}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends and Shift+Enter breaks the line, which is what
              // every message box does — and the reason a textarea is used
              // rather than an input, which cannot hold a second line at all.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (draft.trim()) {
                  submit(draft);
                }
              }
            }}
          />
          <button
            type="submit"
            className="icon-btn composer__send"
            disabled={!draft.trim() || closed}
            aria-label="Send"
          >
            <Icon name="send" size={20} />
          </button>
        </div>
      </form>
    </div>
  );
}
